import merge from 'lodash/merge';
import trimEnd from 'lodash/trimEnd';

import { isNotEmptyString } from '../services/checks';
import { Nullable } from '../services/object';

export type Env = 'dev' | 'preprod' | 'prod' | 'test';

export interface ReadOnlyConfig {
  name: string;
  titleName: string;
  service: string;
  env: Env;
  isProd: boolean;
  isLocalDevelopment: boolean;
  isBrowser: boolean;
}

export interface KeycloakConfig {
  url: string;
  realm: string;
  clientID: string;
}

export interface RuntimeConfig {
  baseUrl: string;
  basePath: string;
  apiUrl: string;
  domain: string;
  emailDomain: string;
  version: string;
  release: string;
  locale?: string;
  country: string;
  keycloak: KeycloakConfig;
  appVersionStatusUrl: string;
  git: Readonly<{
    branch: string;
    commit: string;
  }>;
}

export interface Config extends ReadOnlyConfig, RuntimeConfig {}

export const extractEnv = (version: Nullable<string>, nodeEnv: string): Env => {
  const parts = version?.split('@') ?? [];

  if (parts.length !== 2) {
    if (nodeEnv === 'test') {
      return 'test';
    }

    if (nodeEnv !== 'production') {
      return 'dev';
    }

    return 'prod';
  }

  return (parts[0] ?? 'dev') as Env;
};

export const removeEnvFromVersion = (version: string, env: string): string => {
  if (version.includes('@')) {
    const [, parsedVersion] = version.split('@');
    return parsedVersion;
  }

  return version.replace(env, '');
};

type ProcessEnvReturnType = Readonly<{
  runtimeConfig: RuntimeConfig;
  readOnlyConfig: ReadOnlyConfig;
}>;

export const getBuildTimeProcessEnv = ()=> ({
  NODE_ENV: process.env.NODE_ENV,
  PUBLIC_URL: process.env.PUBLIC_URL,
  REACT_APP_BASEPATH: process.env.REACT_APP_BASEPATH,
  REACT_APP_API_URL: process.env.REACT_APP_API_URL,
  REACT_APP_LOCALE: process.env.REACT_APP_LOCALE,
  REACT_APP_COUNTRY: process.env.REACT_APP_COUNTRY,
  REACT_APP_SERVICE: process.env.REACT_APP_SERVICE,
  REACT_APP_VERSION: process.env.REACT_APP_VERSION,
  REACT_APP_NAME: process.env.REACT_APP_NAME,
  REACT_APP_TITLE: process.env.REACT_APP_TITLE,
  REACT_APP_DOMAIN: process.env.REACT_APP_DOMAIN,
  REACT_APP_KEYCLOAK_URL: process.env.REACT_APP_KEYCLOAK_URL,
  REACT_APP_KEYCLOAK_REALM: process.env.REACT_APP_KEYCLOAK_REALM,
  REACT_APP_KEYCLOAK_CLIENTID: process.env.REACT_APP_KEYCLOAK_CLIENTID,
  REACT_APP_VERSION_STATUS_URL: process.env.REACT_APP_VERSION_STATUS_URL,
  REACT_APP_BUILD_COMMIT: process.env.REACT_APP_BUILD_COMMIT,
});

// eslint-disable-next-line no-undef
export const processEnv = (env: NodeJS.ProcessEnv, defaultTitle: string): ProcessEnvReturnType => {
  const {
    REACT_APP_BASEPATH,
    REACT_APP_API_URL,
    REACT_APP_LOCALE,
    REACT_APP_COUNTRY,
    REACT_APP_SERVICE = 'frontend',
    REACT_APP_VERSION = 'unknown',
    REACT_APP_NAME = 'NFS',
    REACT_APP_TITLE = defaultTitle,
    REACT_APP_DOMAIN,
    NODE_ENV,
    REACT_APP_KEYCLOAK_URL,
    REACT_APP_KEYCLOAK_REALM,
    REACT_APP_KEYCLOAK_CLIENTID,
    REACT_APP_VERSION_STATUS_URL,
  } = env;

  const locale = REACT_APP_LOCALE ?? 'en_gb';
  const country = REACT_APP_COUNTRY ?? 'uk';

  const parsedEnv = extractEnv(REACT_APP_VERSION, NODE_ENV ?? 'development');

  const readOnlyConfig: ReadOnlyConfig = {
    name: REACT_APP_NAME,
    titleName: REACT_APP_TITLE,
    service: REACT_APP_SERVICE,
    env: parsedEnv,
    isProd: parsedEnv === 'prod',
    // different from env - one of the stages of production can be "dev",
    // but that doesn't make it the same as local development
    isLocalDevelopment: NODE_ENV !== 'production',
    isBrowser: typeof window !== 'undefined',
  };

  if (!isNotEmptyString(REACT_APP_KEYCLOAK_URL)) {
    throw new Error('Keycloak URL has to be configured. Make sure you set up REACT_APP_KEYCLOAK_URL correctly.');
  }

  if (!isNotEmptyString(REACT_APP_KEYCLOAK_REALM)) {
    throw new Error('Keycloak\'s realm has to be configured. Make sure you set up REACT_APP_KEYCLOAK_REALM correctly.');
  }

  if (!isNotEmptyString(REACT_APP_KEYCLOAK_CLIENTID)) {
    throw new Error('Keycloak\'s client ID has to be configured. Make sure you set up REACT_APP_KEYCLOAK_CLIENTID correctly.');
  }

  if (!isNotEmptyString(REACT_APP_DOMAIN)) {
    throw new Error('App\'s domain has to be configured. Make sure you set up REACT_APP_DOMAIN correctly.');
  }

  const defaultKeycloakConfig: KeycloakConfig = {
    url: REACT_APP_KEYCLOAK_URL,
    realm: REACT_APP_KEYCLOAK_REALM,
    clientID: REACT_APP_KEYCLOAK_CLIENTID,
  };

  const runtimeConfig: RuntimeConfig = {
    locale,
    country,
    basePath: trimEnd(REACT_APP_BASEPATH ?? '', '/'),
    apiUrl: REACT_APP_API_URL ?? '/api',
    domain: REACT_APP_DOMAIN,
    emailDomain: REACT_APP_DOMAIN ? `@${REACT_APP_DOMAIN}` : '@example.com',
    keycloak: defaultKeycloakConfig,
    appVersionStatusUrl: REACT_APP_VERSION_STATUS_URL ?? '/status/version',
    baseUrl: '', // will be updated later, see below
    release: '',
    version: '',
    git: {
      branch: '',
      commit: '',
    },
  };

  return { readOnlyConfig, runtimeConfig };
};

export const mergeWindowConfig = (windowConfig: Partial<RuntimeConfig>, envConfigs: ProcessEnvReturnType): Config => {
  const config: Config = merge(envConfigs.runtimeConfig, windowConfig, envConfigs.readOnlyConfig);

  // Re-derive env from the runtime-injected version (nginx envsubst populates windowConfig.version
  // from REACT_APP_VERSION). Doing this here instead of at build time lets one build serve every
  // deployment target.
  const runtimeEnv = extractEnv(windowConfig.version ?? '', process.env.NODE_ENV ?? '');
  config.env = runtimeEnv;
  config.isProd = runtimeEnv === 'prod';

  // update fields
  config.baseUrl = `${trimEnd(window.location.origin, '/')}${config.basePath}`;
  // Use the SHA baked into the bundle at build time, not the runtime-injected one. Otherwise
  // AppVersionGuard compares two values that both come from `config.js`/`/status/version` (i.e.
  // from whichever container the LB happens to route to), and during a rolling deploy or with
  // CDN caches mid-rotation the comparison loops instead of converging.
  config.version = isNotEmptyString(process.env.REACT_APP_BUILD_COMMIT)
    ? process.env.REACT_APP_BUILD_COMMIT
    : config.git.commit;
  config.release = `${config.service}@${config.version}`;

  return config;
};

declare global {
  interface Window {
    config?: Partial<RuntimeConfig>;
  }
}

// config.template.js fills window.config at deploy time so that one build can serve every
// environment, which is why the runtime value wins over the one baked into the bundle.
export const resolveApiUrl = (): string => {
  const runtimeApiUrl = window.config?.apiUrl;

  return isNotEmptyString(runtimeApiUrl) ? runtimeApiUrl : process.env.REACT_APP_API_URL ?? '/api';
};
